import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, Facebook, Linkedin, Instagram, ChevronDown } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useLocale } from "../context/LocaleContext";
import { LOGO_URL } from "../mock";

const Sidebar = () => {
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const { t, lang, cartCount } = useShop();
  const { lp, altUrls } = useLocale();
  const location = useLocation();

  // Localizza un path IT-canonico preservando l'eventuale hash (es. /servizi#seo).
  const L = (to) => {
    const [p, h] = to.split("#");
    return lp(p) + (h ? `#${h}` : "");
  };

  // close menu on navigation
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const subLabels = {
    it: {
      agencyMain: "Agenzia web e comunicazione",
      team: "Il nostro team",
      servicesMain: "Servizi su misura",
      seo: "SEO e statistiche",
      app: "Sviluppo APP personalizzate",
      software: "Software, moduli e plugins",
      assistance: "Assistenza",
    },
    en: {
      agencyMain: "Web & communication agency",
      team: "Our team",
      servicesMain: "Tailor-made services",
      seo: "SEO & statistics",
      app: "Custom APP development",
      software: "Software, modules & plugins",
      assistance: "IT support",
    },
  };
  const sl = subLabels[lang];

  const navItems = [
    { to: "/", label: t("home") },
    {
      key: "agency",
      label: t("agency"),
      children: [
        { to: "/agenzia", label: sl.agencyMain },
        { to: "/team", label: sl.team },
      ],
    },
    {
      key: "services",
      label: t("services"),
      children: [
        { to: "/servizi", label: sl.servicesMain },
        { to: "/servizi#seo", label: sl.seo },
        { to: "/servizi#app", label: sl.app },
        { to: "/servizi#software", label: sl.software },
        { to: "/servizi#assistenza", label: sl.assistance },
      ],
    },
    { to: "/portfolio", label: t("portfolio") },
    { to: "/shop", label: t("shop") },
    { to: "/contatti", label: t("contact") },
    { to: "/account", label: lang === "it" ? "Area Clienti" : "Customer Area" },
  ];

  return (
    <>
      {/* Mobile top bar (portrait) */}
      <header className="idm-mobile-topbar" data-testid="mobile-topbar">
        <button
          aria-label="Open menu"
          className="idm-mtb-btn"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu size={22} strokeWidth={2.2} />
        </button>
        <Link to={L("/")} className="idm-mtb-logo" data-testid="mobile-logo">
          <img src="/images/logo-marker.png" alt="ideamedia web agency" />
        </Link>
        <Link to={L("/cart")} className="idm-mtb-btn idm-mtb-cart" data-testid="mobile-cart-link" aria-label="Cart">
          <ShoppingCart size={20} strokeWidth={2} />
          {cartCount > 0 && <span className="idm-cart-badge">{cartCount}</span>}
        </Link>
      </header>

      {/* Collapsed narrow vertical bar - Bridge style */}
      <div className="idm-vbar">
        <button
          aria-label="Open menu"
          className="idm-hamburger"
          onClick={() => setOpen(true)}
          data-testid="sidebar-open-btn"
        >
          <Menu size={23} strokeWidth={2.2} />
        </button>

        <Link to={L("/cart")} className="idm-vbar-cart" data-testid="vbar-cart-link" aria-label="Cart">
          <ShoppingCart size={19} strokeWidth={2} />
          {cartCount > 0 && <span className="idm-cart-badge">{cartCount}</span>}
        </Link>

        <div className="idm-vbar-logo">
          <Link to={L("/")}>
            <img src={LOGO_URL} alt="ideamedia" />
          </Link>
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`idm-overlay ${open ? "visible" : ""}`}
        onClick={() => setOpen(false)}
      />

      {/* Expanded vertical menu 260px - Bridge style */}
      <aside className={`idm-vmenu ${open ? "open" : ""}`} data-testid="vertical-menu">
        <button
          aria-label="Close menu"
          className="idm-close"
          onClick={() => setOpen(false)}
          data-testid="sidebar-close-btn"
        >
          <X size={22} strokeWidth={2} />
        </button>

        <div className="idm-vmenu-logo">
          <Link to={L("/")}>
            <img src="/images/logo-full.png" alt="IdeaMedia - Web & Communication Agency" />
          </Link>
        </div>

        <nav className="idm-vmenu-nav">
          <ul>
            {navItems.map((item) =>
              item.children ? (
                <li key={item.key} className={`idm-has-sub ${openSub === item.key ? "sub-open" : ""}`}>
                  <button
                    className="idm-sub-toggle"
                    onClick={() => setOpenSub(openSub === item.key ? null : item.key)}
                    data-testid={`nav-sub-${item.key}`}
                  >
                    {item.label}
                    <ChevronDown size={14} className="idm-sub-chevron" />
                  </button>
                  <ul className="idm-submenu">
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <Link to={L(child.to)} data-testid={`subnav-${child.to.replace(/[/#]/g, "-")}`}>
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={item.to}>
                  <NavLink
                    to={L(item.to)}
                    end={item.to === "/"}
                    className={({ isActive }) => (isActive ? "active" : "")}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>

        {/* Language switcher: naviga all'URL equivalente nell'altra lingua */}
        <div className="idm-lang-switch" data-testid="lang-switcher">
          <Link
            to={altUrls.it}
            className={lang === "it" ? "active" : ""}
            data-testid="lang-it"
          >
            IT
          </Link>
          <span>/</span>
          <Link
            to={altUrls.en}
            className={lang === "en" ? "active" : ""}
            data-testid="lang-en"
          >
            EN
          </Link>
        </div>

        <div className="idm-vmenu-social">
          <a href="https://www.facebook.com/Ideamedia.agency" target="_blank" rel="noreferrer" aria-label="Facebook">
            <Facebook size={15} />
          </a>
          <a href="https://www.linkedin.com/company/ideamediaweb/" target="_blank" rel="noreferrer" aria-label="LinkedIn">
            <Linkedin size={15} />
          </a>
          <a href="https://www.instagram.com/ideamediawebagency/" target="_blank" rel="noreferrer" aria-label="Instagram">
            <Instagram size={15} />
          </a>
        </div>

        <p className="idm-vmenu-vat" data-testid="sidebar-vat">p.iva it05117210285</p>
      </aside>
    </>
  );
};

export default Sidebar;
